import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { OptionalAgreementsType } from 'store/api-slice/types'

export type ContactField =
    | 'name'
    | 'surname'
    | 'email'
    | 'confirmEmail'
    | 'phone'
export type RegisterField = 'password'
export type TermsField = 'isRequitedTermsAccepted'
export type InvoiceField =
    | 'companyName'
    | 'nip'
    | 'street'
    | 'apartment'
    | 'postCode'
    | 'city'
export type PersonalInvoiceField = 'street' | 'apartment' | 'postCode' | 'city'
export type ShippingField =
    | 'street'
    | 'apartment'
    | 'postCode'
    | 'city'
    | 'name'
    | 'surname'
    | 'email'
    | 'phone'
    | 'signature'

export type AccommodationDetailField =
    | 'customerFirstName'
    | 'customerLastName'
    | 'customerEmail'
    | 'customerPhone'

export interface FieldError {
    property_path: string
    message: string
}

interface OrderState {
    contact: {
        name: string
        surname: string
        email: string
        confirmEmail: string
        phone: string
    }
    register: {
        isChecked: boolean
        password: string
    }
    invoice: {
        isChecked: boolean
        companyName: string
        nip: string
        street: string
        apartment: string
        postCode: string
        city: string
    }
    personalInvoice: {
        isChecked: boolean
        street: string
        apartment: string
        postCode: string
        city: string
    }
    shipping: {
        deliveryType: 'email' | 'shipping'
        name: string
        surname: string
        email: string
        phone: string
        street: string
        apartment: string
        postCode: string
        city: string
        signature: string[]
    }
    isAgreementChecked: boolean
    errors: FieldError[]
    optionalAgreements: OptionalAgreementsType[]
}

const initialState: OrderState = {
    contact: {
        name: '',
        surname: '',
        email: '',
        confirmEmail: '',
        phone: '',
    },
    register: {
        isChecked: false,
        password: '',
    },
    invoice: {
        isChecked: false,
        companyName: '',
        nip: '',
        street: '',
        apartment: '',
        postCode: '',
        city: '',
    },
    personalInvoice: {
        isChecked: false,
        street: '',
        apartment: '',
        postCode: '',
        city: '',
    },
    shipping: {
        deliveryType: 'email',
        name: '',
        surname: '',
        email: '',
        phone: '',
        street: '',
        apartment: '',
        postCode: '',
        city: '',
        signature: [],
    },
    isAgreementChecked: false,
    errors: [],
    optionalAgreements: [],
}

const OrderSlice = createSlice({
    name: 'Order',
    initialState,
    reducers: {
        setContactInfo(
            state,
            action: PayloadAction<{
                field: ContactField
                value: string
            }>
        ) {
            const { field, value } = action.payload
            state.contact[field] = value
        },
        setRegisterInfo(
            state,
            action: PayloadAction<{
                field: RegisterField
                value: string
            }>
        ) {
            const { field, value } = action.payload
            state.register[field] = value
        },
        setInvoiceInfo(
            state,
            action: PayloadAction<{
                field: InvoiceField | PersonalInvoiceField
                value: string
            }>
        ) {
            const { field, value } = action.payload

            if (
                field === 'companyName' ||
                field === 'nip' ||
                state.invoice.isChecked
            ) {
                state.invoice[field] = value
            } else {
                state.personalInvoice[field] = value
            }
        },
        setShippingInfo(
            state,
            action: PayloadAction<{
                field: ShippingField
                value: string | string[]
            }>
        ) {
            const { field, value } = action.payload

            if (typeof value === 'object') {
                state.shipping.signature = value
            }

            if (field !== 'signature' && typeof value === 'string') {
                state.shipping[field] = value
            }
        },
        setDeliveryType(state, action: PayloadAction<'email' | 'shipping'>) {
            state.shipping.deliveryType = action.payload
        },
        toggleOrderInfoAdditionalModule(
            state,
            action: PayloadAction<{
                field: 'register' | 'invoice' | 'personalInvoice'
                isChecked: boolean
            }>
        ) {
            const { field, isChecked } = action.payload
            state[field].isChecked = isChecked
        },
        toggleAgreement(state, action: PayloadAction<boolean>) {
            state.isAgreementChecked = action.payload
        },
        setErrors(state, action: PayloadAction<FieldError[]>) {
            state.errors = action.payload
        },
        removeError(state, action: PayloadAction<string>) {
            const errors = [...state.errors]
            const indexToRemove = errors.findIndex((error) =>
                error.property_path.includes(action.payload)
            )

            errors.splice(indexToRemove, 1)
            state.errors = errors
        },
        setOptionalAgreements(
            state,
            action: PayloadAction<OptionalAgreementsType[]>
        ) {
            state.optionalAgreements = action.payload
        },
    },
})

export const {
    setContactInfo,
    setRegisterInfo,
    setInvoiceInfo,
    setShippingInfo,
    setDeliveryType,
    toggleOrderInfoAdditionalModule,
    toggleAgreement,
    setErrors,
    removeError,
    setOptionalAgreements,
} = OrderSlice.actions
export default OrderSlice.reducer
